<template>
  <div :class="textAlign" simple-text-component :style="[theme, style]">
    <div v-if="content.counter_label" class="counter mb-3 d-flex align-items-center justify-content-center">
      {{content.counter_label}}
    </div>
    <h5 v-if="content.category" category v-html="content.category"></h5>
    <h2 v-if="content.title" :class="titleClass" title v-html="content.title"></h2>
    <h3 v-if="content.subtitle" :class="subtitleClass" subtitle v-html="content.subtitle"></h3>
    <div :class="content.description_class" v-if="content.description" v-html="content.description" description></div>
    <div v-if="content.button" class="pt-4">
      <a 
        class="btn btn-primary m-2 d-inline-block" 
        :target="getTarget(content.button)"
        :class="content.button_class"
        :href="content.button.url"
        @click.prevent="handleButtonClick($event)"
      >
        <i class="fa-light fa-headset mr-2"></i>
        {{content.button.title}}
      </a>
    </div>
    <svg
      v-if="content.show_arrow"
      :style="arrowStyle"
      :class="{animate: activeStory}"
      class="arrow w-100 text-center mt-4"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 33.45 239.06"
    >
      <line x1="16.72" y1="230.5" x2="16.72" y2="2.5"/>
      <polyline class="arrowhead" points="30.95 221.1 16.72 235.5 2.5 221.1"/>
    </svg>
  </div>
</template>

<script>
import { fandomBasicMixin, fandomExtendedMixin } from "src/modules/fandom_mixins_module.js";
import anime from 'animejs/lib/anime.es.js';

export default {
  mixins: [fandomBasicMixin, fandomExtendedMixin],
  props: {
    activeStory: {
      type: Boolean
    }
  },
  data: function() {
    return {
      durationPerCharacter: 65,
      animations: {
        list: [],
        currentIndex: 0
      }
    }
  },
  mounted() {
    this.initAllAnimations();
    if (this.activeStory) {
      this.resetAllAnimations();
      this.playAnimation();
    }
  },
  computed: {
    arrowStyle() {
      return {
        "--arrow-stroke-color": this.content.arrow_color || "var(--white)"
      }
    },
    textAlign() {
      return (this.content.text_align) ? `text-${this.content.text_align}` : null;
    },
    titleClass() {
      return this.content.title_class;
    },
    subtitleClass() {
      return this.content.subtitle_class;
    },
    style() {
      return {
        "--mark-color": this.content.mark_color || "var(--theme-color)",
        "--mark-background": this.content.mark_background || "var(--theme-background)"
      };
    }
  },
  watch: {
    activeStory(val) {
      if (val) {
        this.resetAllAnimations();
        this.playAnimation();
      } else {
        // nothing to do
      }
    }
  },
  methods: {
    initAllAnimations() {
      const targets = this.$el.querySelectorAll(".counter, [category], [title], [description], [subtitle], button, .btn");

      targets.forEach((element, index) => {
        this.initAnimation(element, index);
      });
    },
    initAnimation(element, index) {
      this.animations.list[index] = anime({
        targets: element,
        opacity: {
          value: [0, 1],
          easing: 'easeInOutCubic'
        },
        translateY: {
          value: ['85%', 0],
          easing: 'easeOutQuint'
        },
        duration: 1000,
        autoplay: false,
        begin: () => setTimeout(() => this.animateElementMarks(element, this.nextAnimation), 500)
      })
    },
    resetAllAnimations() {
      this.animations.currentIndex = 0;
      this.animations.list.forEach((element) => element.seek(0));
      this.clearAllMarks();
    },
    playAnimation() {
      this.animations.list[this.animations.currentIndex].play();
    },
    nextAnimation() {
      if (this.animations.currentIndex < this.animations.list.length - 1) {
        this.animations.currentIndex++;
        this.playAnimation();
      }
    },
    animateElementMarks(element, callback) {
      const marks = element.getElementsByTagName('mark');

      if (marks.length > 0) {
        this.animateMark(marks, 0, callback);
      } else {
        callback();
      }
    },
    animateMark(marks, index = 0, callback) {
      const textLength = marks[index].textContent.length;
      const animationDuration = this.durationPerCharacter * textLength;

      marks[index].style.animationDuration = `${animationDuration}ms`;
      marks[index].classList.add('animate');

      if (index < marks.length - 1) {
        setTimeout(() => this.animateMark(marks, ++index, callback), animationDuration);
      } else {
        setTimeout(() => callback(), animationDuration);
      }
    },
    clearAllMarks() {
      this.$el.querySelectorAll('mark.animate').forEach((mark) => {
        mark.classList.remove('animate');
      })
    },
    handleButtonClick(event) {
      if (!!window.wa_gc) {
        const digitalData = Fandom.deepClone(window.digitalData);
        digitalData.link_name = window.truncatedPageName + ":CTC";
        window.wa_gc(digitalData, "link");
        console.log("%cExecuted wa_gc(digitalData, 'link')", "background: #222; color: #bada55; padding: .5rem;", digitalData);
      }

      if (this.content.button.url === '$contactForm') {
        $('#modal-contact-form').modal('show');
        Fandom.emit('content-player-pause');
      } else {
        this.trackingButtonUrl(event);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
[simple-text-component] {
  .counter {
    width: 3rem;
    height: 3rem;
    padding: 0rem .75rem;
    border-radius: 50%;
    background-color: var(--theme-color);
    color: var(--theme-background);
    font-family: 'VodafoneEx';
    font-size: 1.5rem;
    font-weight: 400;
  }

  ::v-deep mark {
    color: var(--mark-color);
    background: transparent;    
    padding: 0;

    &.animate {
      background: linear-gradient(90deg, var(--mark-background) 50%, rgba(255, 255, 255, 0) 50%);
      background-size: 200% 100%;
      background-position: 100% 0;

      animation-name: highlight;
      animation-timing-function: ease;
      animation-fill-mode: forwards;
    }
  }

  [title], [subtitle], [category], [description] {
    font-family: 'VodafoneEx';
    opacity: 0;
  }

  [title] {
    font-size: 5rem;
  }

  [subtitle] {
    font-size: 3.5rem;
  }

  [description] {
    font-size: 2.25rem;
  }

  .huge-text {
    font-size: 16rem;
    line-height: normal;
  }

  .text-stroke {
    -webkit-text-stroke: 2px var(--theme-color, $black);
  }

  .arrow {
    height: 10rem;
    fill: none;
    stroke: var(--arrow-stroke-color);
    stroke-width: 4px;
    stroke-linecap: round;
    stroke-dasharray: 1000;
    stroke-dashoffset: 1772; // inital value

    &.animate {
      animation-name: arrow-show;
      animation-duration: 1000ms;
      animation-timing-function: ease;
      animation-fill-mode: forwards;

      .arrowhead {
        animation-delay: 100ms;
      }
    }

   
  }

  @include media-breakpoint-down(md) {
    [title] {
      font-size: 6.25vh;
    }

    [subtitle] {
      font-size: 4.75vh;
    }

    [description] {
      font-size: 3.25vh;
      line-height: 4.5vh;
    }

    [category] {
      font-size: 3vh;
    }

    .huge-text {
      font-size: 18vh;
    }
  }

  @keyframes highlight {
    to {
      background-position: 0 0;
    }
  }

  @keyframes arrow-show {
    from { stroke-dashoffset: 1772; }
    to { stroke-dashoffset: 2000; }
  }
}
</style>
